import type { Changes, CollectionKey, CollectionOp, JSONSchema, ServiceSchema, UnsetChange } from "@models/mappingConfig";
import { describeFields, type FieldDescriptor } from "./schema";

/**
 * Builds the `changes` body of POST/PATCH `.../versions` (contract A.3) from the
 * form's baseline and edited values:
 *
 * - only fields whose value differs are sent;
 * - a sub-model (`object` with properties) is deep-merged server side, so only
 *   its changed leaves are sent;
 * - a collection (array with `x-key`) is sent as `$ops` (remove / update with
 *   only the changed item fields / add);
 * - a field reset to its default carries the `{ $unset: true }` marker;
 * - a code-owned field (`x-owner: code`) is never sent.
 */

export const UNSET: UnsetChange = Object.freeze({ $unset: true as const });

export const isUnset = (value: unknown): value is UnsetChange =>
  typeof value === "object" && value !== null && (value as { $unset?: unknown }).$unset === true;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const deepEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (typeof a !== typeof b || a === null || b === null || typeof a !== "object") {
    // NaN, and treat undefined/absent as unequal to null on purpose.
    return Number.isNaN(a) && Number.isNaN(b);
  }
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) {
    const other = b as unknown[];
    return a.length === other.length && a.every((v, i) => deepEqual(v, other[i]));
  }
  const ao = a as Record<string, unknown>;
  const bo = b as Record<string, unknown>;
  const keys = new Set([...Object.keys(ao), ...Object.keys(bo)]);
  for (const key of keys) {
    if (!deepEqual(ao[key], bo[key])) return false;
  }
  return true;
};

/** Normalises a key for matching: 17122 and "17122" address the same entry. */
export const keyString = (key: unknown): string => (key === null || key === undefined ? "" : String(key));

/** `$ops` that turn `original` into `items`, matched on `key`. Order: remove, update, add. */
export const collectionOps = (
  original: Record<string, unknown>[],
  items: Record<string, unknown>[],
  key: string,
): CollectionOp[] => {
  const before = new Map(original.map((item) => [keyString(item[key]), item]));
  const after = new Map(items.map((item) => [keyString(item[key]), item]));
  const removes: CollectionOp[] = [];
  const updates: CollectionOp[] = [];
  const adds: CollectionOp[] = [];

  for (const [k, item] of before) {
    if (!after.has(k)) removes.push({ op: "remove", key: item[key] as CollectionKey });
  }
  for (const [k, item] of after) {
    const previous = before.get(k);
    if (!previous) {
      adds.push({ op: "add", item });
      continue;
    }
    const set: Record<string, unknown> = {};
    for (const field of Object.keys(item)) {
      if (field === key) continue;
      if (!deepEqual(item[field], previous[field])) set[field] = item[field];
    }
    if (Object.keys(set).length > 0) updates.push({ op: "update", key: previous[key] as CollectionKey, set });
  }
  return [...removes, ...updates, ...adds];
};

/** Changed leaves of a sub-model, recursing into nested sub-models; `undefined` when nothing changed. */
const objectChanges = (
  descriptor: FieldDescriptor,
  original: Record<string, unknown>,
  edited: Record<string, unknown>,
): Record<string, unknown> | undefined => {
  const out: Record<string, unknown> = {};
  for (const child of descriptor.children ?? []) {
    if (child.owner === "code") continue;
    const nested = fieldChange(child, original[child.name], edited[child.name], child.name in edited);
    if (nested !== NO_CHANGE) out[child.name] = nested;
  }
  return Object.keys(out).length > 0 ? out : undefined;
};

const NO_CHANGE = Symbol("no-change");

const fieldChange = (
  descriptor: FieldDescriptor,
  original: unknown,
  edited: unknown,
  present: boolean,
): unknown | typeof NO_CHANGE => {
  if (descriptor.owner === "code" || !present) return NO_CHANGE;
  if (isUnset(edited)) return isUnset(original) ? NO_CHANGE : { $unset: true };
  if (deepEqual(original, edited)) return NO_CHANGE;

  if (descriptor.kind === "collection" && descriptor.collectionKey && Array.isArray(edited)) {
    const ops = collectionOps(
      (Array.isArray(original) ? original : []) as Record<string, unknown>[],
      edited as Record<string, unknown>[],
      descriptor.collectionKey,
    );
    return ops.length > 0 ? { $ops: ops } : NO_CHANGE;
  }
  if (descriptor.kind === "object" && isPlainObject(original) && isPlainObject(edited)) {
    return objectChanges(descriptor, original, edited) ?? NO_CHANGE;
  }
  return edited;
};

/** The `changes` turning `original` into `edited` (both bodies keyed like `json_schema.properties`). */
export const buildChanges = (
  schema: ServiceSchema | JSONSchema,
  original: Record<string, unknown>,
  edited: Record<string, unknown>,
): Changes => {
  const jsonSchema = "json_schema" in schema ? (schema as ServiceSchema).json_schema : (schema as JSONSchema);
  const changes: Changes = {};
  for (const descriptor of describeFields(jsonSchema)) {
    const change = fieldChange(descriptor, original[descriptor.name], edited[descriptor.name], descriptor.name in edited);
    if (change !== NO_CHANGE) changes[descriptor.name] = change;
  }
  return changes;
};

const applyOps = (items: Record<string, unknown>[], ops: CollectionOp[], key: string): Record<string, unknown>[] => {
  let next = [...items];
  for (const op of ops) {
    if (op.op === "remove") {
      next = next.filter((item) => keyString(item[key]) !== keyString(op.key));
    } else if (op.op === "update") {
      next = next.map((item) => (keyString(item[key]) === keyString(op.key) ? { ...item, ...op.set } : item));
    } else {
      const k = keyString(op.item[key]);
      const index = next.findIndex((item) => keyString(item[key]) === k);
      if (index >= 0) next[index] = { ...next[index], ...op.item };
      else next.push(op.item);
    }
  }
  return next;
};

const applyField = (descriptor: FieldDescriptor, current: unknown, change: unknown): unknown => {
  if (isUnset(change)) return UNSET;
  if (isPlainObject(change) && Array.isArray(change.$ops) && descriptor.collectionKey) {
    return applyOps((Array.isArray(current) ? current : []) as Record<string, unknown>[], change.$ops as CollectionOp[],
                    descriptor.collectionKey);
  }
  if (isPlainObject(change) && Array.isArray(change.$replace)) return change.$replace;
  if (descriptor.kind === "object" && isPlainObject(change)) {
    const base = isPlainObject(current) ? { ...current } : {};
    for (const child of descriptor.children ?? []) {
      if (child.name in change) base[child.name] = applyField(child, base[child.name], change[child.name]);
    }
    return base;
  }
  return change;
};

/**
 * Replays `changes` onto a (newer) body: how the edit page keeps someone's
 * unsaved work when a 409 makes it reload the draft, without reverting the
 * fields the other person changed.
 */
export const applyChanges = (
  schema: ServiceSchema | JSONSchema,
  body: Record<string, unknown>,
  changes: Changes,
): Record<string, unknown> => {
  const jsonSchema = "json_schema" in schema ? (schema as ServiceSchema).json_schema : (schema as JSONSchema);
  const next = { ...body };
  for (const descriptor of describeFields(jsonSchema)) {
    if (descriptor.name in changes) next[descriptor.name] = applyField(descriptor, next[descriptor.name], changes[descriptor.name]);
  }
  return next;
};

export const hasChanges = (changes: Changes): boolean => Object.keys(changes).length > 0;
